import React, { useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Button,
    TextField,
    Checkbox,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Chip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MDBox from "components/MDBox";
import MDAlert from "../../components/MDAlert";
import MDTypography from "../../components/MDTypography";
import { AuthContext } from "../../context";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import apiService from "../../services/api-service";
import toast from "react-hot-toast";

function RoleEdit() {
    const authContext = useContext(AuthContext);
    const navigate = useNavigate();
    const { id } = useParams();
    
    const [roleData, setRoleData] = useState({
        name: '',
        description: ''
    });
    const [selectedAuths, setSelectedAuths] = useState([]);
    const [availableAuths, setAvailableAuths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch role data and available authorizations in parallel
                const [roleResponse, authsResponse] = await Promise.all([
                    apiService.get("/role-management/", { id }, {}),
                    apiService.get("/authorizations/", {}, {})
                ]);

                // Set role data
                if (roleResponse.inf) {
                    setRoleData({
                        name: roleResponse.inf.name,
                        description: roleResponse.inf.description
                    });
                    setSelectedAuths(roleResponse.inf.auths || []);
                }

                // Set available authorizations
                setAvailableAuths(authsResponse);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching data:", error);
                setError("Errore durante il caricamento dei dati del ruolo");
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const handleInputChange = useCallback((field, value) => {
        setRoleData(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const handleAuthToggle = useCallback((authId) => {
        setSelectedAuths(prev => {
            if (prev.includes(authId)) {
                return prev.filter(id => id !== authId);
            } else {
                return [...prev, authId];
            }
        });
    }, []);

    const handleSave = async () => {
        if (!roleData.name || !roleData.description) {
            setError("Nome e Descrizione sono obbligatori");
            return;
        }

        setSaving(true);
        try {
            const payload = {
                id: parseInt(id),
                name: roleData.name,
                description: roleData.description,
                auths: selectedAuths,
                is_yard_responsable: false,
                is_security_responsable: false,
                is_supplier_responsable: false,
                is_dl: false,
                is_site_manager_technician: false
            };

            await apiService.post("/role-management/", {}, payload);
            toast.success("Ruolo modificato con successo");
            navigate("/role-managment");
        } catch (error) {
            console.error("Error saving role:", error);
            setError("Errore durante il salvataggio del ruolo");
        }
        setSaving(false);
    };

    // Filter authorizations based on search term
    const filteredAuths = useMemo(() => {
        if (!searchTerm.trim()) {
            return availableAuths;
        }
        const searchLower = searchTerm.toLowerCase();
        return availableAuths.filter(auth =>
            auth.name?.toLowerCase().includes(searchLower) ||
            auth.description?.toLowerCase().includes(searchLower)
        );
    }, [availableAuths, searchTerm]);

    const handleSelectAllFiltered = useCallback(() => {
        const filteredIds = filteredAuths.map(auth => auth.id);
        const newSelected = [...new Set([...selectedAuths, ...filteredIds])];
        setSelectedAuths(newSelected);
    }, [filteredAuths, selectedAuths]);

    const handleDeselectAllFiltered = useCallback(() => {
        const filteredIds = new Set(filteredAuths.map(auth => auth.id));
        const newSelected = selectedAuths.filter(id => !filteredIds.has(id));
        setSelectedAuths(newSelected);
    }, [filteredAuths, selectedAuths]);

    const handleClearSearch = useCallback(() => {
        setSearchTerm("");
    }, []);

    if (loading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} display="flex" justifyContent="center">
                    <CircularProgress />
                </MDBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            {authContext.hasAuth("EDIT_ROLE") ? (
                <>
                    <DashboardNavbar />
                    <MDBox pt={6} pb={3}>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => navigate("/role-managment")} 
                            sx={{ mt: 2, alignSelf: "center", minWidth: "100px", color: "#ffffff", marginBottom: "30px" }}
                        >
                            Torna alla tabella
                        </Button>

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Modifica Ruolo
                            </Typography>
                            
                            <Box sx={{ mb: 3 }}>
                                <TextField
                                    fullWidth
                                    label="Nome"
                                    value={roleData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    margin="normal"
                                    required
                                />
                                <TextField
                                    fullWidth
                                    label="Descrizione"
                                    value={roleData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    margin="normal"
                                    required
                                />
                            </Box>

                            <Typography variant="h6" gutterBottom>
                                Autorizzazioni
                            </Typography>

                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    placeholder="Cerca autorizzazione per nome o descrizione..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchTerm && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleClearSearch} size="small">
                                                    <ClearIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip
                                        label={`${filteredAuths.length} ${searchTerm ? 'trovate' : 'totali'}`}
                                        color="primary"
                                        size="small"
                                    />
                                    <Chip
                                        label={`${selectedAuths.length} selezionate`}
                                        color="success"
                                        size="small"
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleSelectAllFiltered}
                                        disabled={filteredAuths.length === 0}
                                    >
                                        Seleziona {searchTerm ? 'Filtrate' : 'Tutte'}
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleDeselectAllFiltered}
                                        disabled={filteredAuths.length === 0}
                                    >
                                        Deseleziona {searchTerm ? 'Filtrate' : 'Tutte'}
                                    </Button>
                                </Box>
                            </Box>

                            <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Attivo</TableCell>
                                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Nome Autorizzazione</TableCell>
                                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Descrizione</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredAuths.length > 0 ? (
                                            filteredAuths.map((auth) => (
                                                <TableRow
                                                    key={auth.id}
                                                    hover
                                                    sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                                                >
                                                    <TableCell>
                                                        <Checkbox
                                                            checked={selectedAuths.includes(auth.id)}
                                                            onChange={() => handleAuthToggle(auth.id)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{auth.name}</TableCell>
                                                    <TableCell>{auth.description}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Nessuna autorizzazione trovata
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => navigate("/role-managment")}
                                >
                                    Annulla
                                </Button>
                                <Button 
                                    variant="contained" 
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? <CircularProgress size={24} /> : "Salva"}
                                </Button>
                            </Box>
                        </Paper>
                    </MDBox>
                </>
            ) : (
                <MDAlert color="error" dismissible>
                    <MDTypography variant="body2">
                        Non hai i permessi per accedere a questa pagina
                    </MDTypography>
                </MDAlert>
            )}
        </DashboardLayout>
    );
}

export default RoleEdit;